import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Calendar, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function getStatus() {
  const res = await fetch(`${API}/payments/subscription`, { credentials: "include" });
  if (!res.ok) throw new Error("status failed");
  return res.json();
}

async function cancel() {
  const res = await fetch(`${API}/payments/subscription/cancel`, {
    method: "POST",
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Cancel failed");
  return data;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function SubscriptionCard({ onCancelled }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getStatus();
      setStatus(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCancel = async () => {
    setCancelling(true);
    try {
      const r = await cancel();
      toast.success(r.message || "Subscription cancelled.");
      await load();
      setShowConfirm(false);
      onCancelled?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading || !status || status.plan === "free") return null;

  const isCancelled = Boolean(status.cancelledAt);
  const inTrial = status.inTrial;
  const planLabel = status.plan.charAt(0).toUpperCase() + status.plan.slice(1);

  return (
    <div className="cartiq-card" data-testid="subscription-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
            — Subscription
          </div>
          <h2 className="font-display uppercase text-2xl flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#FF6B00]" /> {planLabel} plan
            {status.cycle && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#888] font-bold ml-1">
                · {status.cycle}
              </span>
            )}
          </h2>
        </div>
        <div
          className={`text-[11px] uppercase tracking-[0.22em] font-bold px-2 py-1 ${
            isCancelled
              ? "text-[#E24B4A] bg-[#E24B4A]18 border border-[#E24B4A]44"
              : inTrial
              ? "text-[#1D9E75] bg-[#1D9E7518] border border-[#1D9E7544]"
              : "text-[#378ADD] bg-[#378ADD18] border border-[#378ADD44]"
          }`}
          data-testid="subscription-status-pill"
        >
          {isCancelled ? "Cancelled" : inTrial ? `${status.trialDaysLeft}d trial` : "Active"}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">
            {inTrial && !isCancelled ? "Trial ends" : "Renews"}
          </div>
          <div
            className="font-mono-num text-base text-[#F0EDE8] flex items-center gap-1.5 mt-1"
            data-testid="subscription-trial-ends"
          >
            <Calendar className="w-3.5 h-3.5 text-[#888]" />
            {formatDate(inTrial ? status.trialEndsAt : status.planAccessUntil)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#888]">
            {isCancelled ? "Access until" : "Days left"}
          </div>
          <div
            className="font-mono-num text-base text-[#F0EDE8] mt-1"
            data-testid="subscription-days-left"
          >
            {isCancelled ? formatDate(status.planAccessUntil) : `${status.daysLeft ?? "—"} days`}
          </div>
        </div>
      </div>

      {isCancelled ? (
        <div
          className="p-3 bg-[#E24B4A]10 border-l-[3px] border-[#E24B4A] text-[13px] text-[#F0EDE8] leading-relaxed"
          data-testid="subscription-cancelled-notice"
        >
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 text-[#E24B4A]" />
          Your subscription is cancelled. You keep full {planLabel} access until{" "}
          <span className="font-bold">{formatDate(status.planAccessUntil)}</span>, then you'll be moved to Free.
        </div>
      ) : !showConfirm ? (
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          className="w-full border border-[#2A2A35] text-[#888] hover:text-[#E24B4A] hover:border-[#E24B4A] font-bold uppercase tracking-wider py-2.5 text-xs transition-colors"
          data-testid="subscription-cancel-btn"
        >
          Cancel subscription
        </button>
      ) : (
        <div className="space-y-2" data-testid="subscription-cancel-confirm">
          <div className="text-[12px] text-[#888] leading-relaxed">
            You'll keep {planLabel} access until{" "}
            <span className="text-[#F0EDE8] font-bold">{formatDate(status.planAccessUntil)}</span>. No refund for the
            current period. Confirm?
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={cancelling}
              className="flex-1 bg-[#E24B4A] text-white font-bold uppercase tracking-wider py-2.5 text-xs disabled:opacity-60"
              data-testid="subscription-cancel-confirm-btn"
            >
              {cancelling ? "Cancelling…" : "Yes, cancel"}
            </button>
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              className="flex-1 border border-[#2A2A35] text-[#888] hover:text-[#F0EDE8] font-bold uppercase tracking-wider py-2.5 text-xs"
              data-testid="subscription-cancel-abort-btn"
            >
              Keep it
            </button>
          </div>
        </div>
      )}

      <div className="text-[10px] uppercase tracking-[0.18em] text-[#555] mt-3">
        15-day free trial · cancel anytime · no refunds for used periods
      </div>
    </div>
  );
}
